/* FAQ V2 — animated accordion + search + category filter */
(() => {
  'use strict';

  /* ─────────────────────────────────────────────
     FaqV2Item  — animated <details> accordion
  ───────────────────────────────────────────── */
  class FaqV2Item extends HTMLElement {
    connectedCallback() {
      this._details  = this.querySelector('.faqv2__details');
      this._summary  = this.querySelector('.faqv2__summary');
      this._answer   = this.querySelector('.faqv2__answer');
      this._anim     = null;
      this._closing  = false;
      this._opening  = false;

      this._summary.addEventListener('click', (e) => this._onClick(e));
    }

    _onClick(e) {
      e.preventDefault();
      this._details.style.overflow = 'hidden';

      if (this._closing || !this._details.open) {
        this._open();
      } else {
        this._close();
      }
    }

    _open() {
      /* Notify parent (single-open mode) */
      this.dispatchEvent(new CustomEvent('faqv2:open', { bubbles: true }));

      this._details.style.height = `${this._details.offsetHeight}px`;
      this._details.open = true;
      this._opening = true;

      requestAnimationFrame(() => {
        const to = this._summary.offsetHeight + this._answer.scrollHeight;
        this._run(this._details.offsetHeight, to, true);
      });
    }

    _close() {
      this._closing = true;
      this._run(
        this._details.offsetHeight,
        this._summary.offsetHeight,
        false
      );
    }

    _run(from, to, opening) {
      if (this._anim) this._anim.cancel();

      this._anim = this._details.animate(
        { height: [`${from}px`, `${to}px`] },
        { duration: 380, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' }
      );

      this._anim.onfinish = () => {
        this._details.open        = opening;
        this._details.style.height   = '';
        this._details.style.overflow = '';
        this._summary.setAttribute('aria-expanded', String(opening));
        this._closing = false;
        this._opening = false;
        this._anim    = null;
      };

      this._anim.oncancel = () => {
        this._closing = false;
        this._opening = false;
      };
    }

    /* Called by parent when single-open mode kicks in */
    closeItem() {
      if (this._details.open && !this._closing) this._close();
    }
  }

  customElements.define('faq-v2-item', FaqV2Item);


  /* ─────────────────────────────────────────────
     FaqV2  — section controller
  ───────────────────────────────────────────── */
  class FaqV2 extends HTMLElement {
    connectedCallback() {
      this._items       = [...this.querySelectorAll('faq-v2-item')];
      this._tabs        = [...this.querySelectorAll('.faqv2__tab')];
      this._input       = this.querySelector('.faqv2__search-input');
      this._clearBtn    = this.querySelector('.faqv2__search-clear');
      this._noResults   = this.querySelector('.faqv2__no-results');
      this._resetBtn    = this.querySelector('.faqv2__no-results-reset');
      this._activeTab   = 'all';
      this._query       = '';
      this._singleOpen  = this.dataset.singleOpen === 'true';

      /* Category tabs */
      this._tabs.forEach((tab) => {
        tab.addEventListener('click', () => this._activateTab(tab.dataset.category));
      });

      /* Search */
      if (this._input) {
        this._input.addEventListener('input', this._debounce(() => {
          this._query = this._input.value.trim().toLowerCase();
          if (this._clearBtn) this._clearBtn.hidden = !this._query;
          this._filter();
        }, 200));
      }

      if (this._clearBtn) {
        this._clearBtn.addEventListener('click', () => {
          this._input.value = '';
          this._query = '';
          this._clearBtn.hidden = true;
          this._input.focus();
          this._filter();
        });
      }

      if (this._resetBtn) {
        this._resetBtn.addEventListener('click', () => {
          if (this._input) {
            this._input.value = '';
            this._query = '';
            if (this._clearBtn) this._clearBtn.hidden = true;
          }
          this._activateTab('all');
        });
      }

      /* Single-open: close siblings when one item opens */
      this.addEventListener('faqv2:open', (e) => {
        if (!this._singleOpen) return;
        this._items.forEach((item) => {
          if (item !== e.target) item.closeItem();
        });
      });
    }

    _activateTab(category) {
      this._activeTab = category;

      this._tabs.forEach((tab) => {
        const active = tab.dataset.category === category;
        tab.classList.toggle('faqv2__tab--active', active);
        tab.setAttribute('aria-selected', String(active));
      });

      this._filter();
    }

    _filter() {
      let shown = 0;

      this._items.forEach((item) => {
        const catOk = this._activeTab === 'all' ||
          item.dataset.category === this._activeTab;

        let textOk = true;
        if (this._query) {
          const q = item.querySelector('.faqv2__question')?.textContent.toLowerCase() ?? '';
          const a = item.querySelector('.faqv2__answer-inner')?.textContent.toLowerCase() ?? '';
          textOk = q.includes(this._query) || a.includes(this._query);
        }

        const visible = catOk && textOk;
        item.hidden = !visible;

        if (visible) {
          item.style.setProperty('--faqv2-stagger', shown);
          /* Restart entry animation */
          item.classList.remove('faqv2__item--entering');
          void item.offsetWidth;
          item.classList.add('faqv2__item--entering');
          shown++;
        }
      });

      if (this._noResults) this._noResults.hidden = shown > 0;
    }

    _debounce(fn, ms) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), ms);
      };
    }
  }

  customElements.define('faq-v2', FaqV2);
})();
